import { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useContractFilterOptions } from 'hooks/moderator/contracts/useContractFilterOptions';
import terms from 'i18n';
import { getIsoDate } from 'utils/date';

import {
  Contract,
  ContractLegalEntityPayerData,
  ContractType,
} from '../../../models/applications/contracts';
import { SelectOption } from '../../../models/common';
import { createToast } from '../../../redux/actions';
import { contractApi } from '../../../redux/api/applications/contract-api';
import useAppDispatch from '../../common/useAppDispatch';
import { useGetContractPaymentTypesSelectProps } from './useGetContractPaymentTypesSelectProps';
import { useGetCreateContractSelectOptions } from './useGetDirectionSelectOptions';

type JuridicalContractForm = ContractLegalEntityPayerData & {
  directionId: SelectOption;
  paymentForm: SelectOption;
  contractDate?: string;
  paymentMethod: SelectOption;
};

const useJuridicalContractForm = (
  isDateFieldShown: boolean,
  contract?: Contract,
  isFillingStatementsForm?: boolean,
  onSubmitProp?: (res) => void
) => {
  const navigate = useNavigate();
  const [createContract] = contractApi.useLazyCreateQuery();
  const [updateContract] = contractApi.useUpdateContractMutation();
  const dispatch = useAppDispatch();

  const directionOptions = useGetCreateContractSelectOptions(
    Boolean(contract),
    contract?.applicationId
  );
  const paymentForms = useGetContractPaymentTypesSelectProps();

  const { paymentMethod } = useContractFilterOptions();
  const paymentMethodOptions = paymentMethod.options;

  const { register, handleSubmit, watch, setValue, getValues, reset, ...rest } =
    useForm<JuridicalContractForm>({ mode: 'all' });

  useEffect(() => {
    if (contract && directionOptions.length && paymentForms.length) {
      if (contract?.legalEntityPayerData) {
        reset({
          ...contract?.legalEntityPayerData,
          directionId: directionOptions.find(
            d => d.value === contract.applicationDirectionId
          ),
          paymentForm: paymentForms.find(
            p => p.value === contract.paymentType.id
          ),
        });
      }

      setValue(
        'directionId',
        directionOptions.find(d => d.value === contract.applicationDirectionId)!
      );

      setValue(
        'paymentForm',
        paymentForms.find(p => p.value === contract.paymentType.id)!
      );
    }
    if (isDateFieldShown) {
      setValue(
        'contractDate',
        contract?.contractDate ?? getIsoDate(new Date())
      );
    }
  }, [directionOptions.length, paymentForms.length, isDateFieldShown]);

  const fields = {
    contractDate: register('contractDate', {
      required: isDateFieldShown && terms.REQUIRED_FIELD,
    }),
    paymentMethod: register('paymentMethod', {
      required: terms.REQUIRED_FIELD,
    }),
  };

  const onSubmit = handleSubmit(data => {
    let request;
    const normalizedData = {
      contractType: ContractType.JURIDICAL,
      paymentTypeId: data.paymentForm.value,
      paymentMethod: data.paymentMethod.value,
      applicationDirectionId: data.directionId.value,
      contractLegalEntityPayerData: {
        ...data,
      },
    };
    const contractDate = data.contractDate;
    if (isDateFieldShown) {
      normalizedData['contractDate'] = contractDate;
    }
    if (contract) {
      normalizedData['id'] = contract.id;
      request = updateContract(normalizedData as any).unwrap();
    } else {
      request = createContract(normalizedData)
        .unwrap()
        .catch(() =>
          dispatch(createToast(terms.CREATE_CONTRACT_ERROR, 'danger'))
        );
    }

    request.then(res => {
      if (isFillingStatementsForm) {
        navigate('..');
      }
      onSubmitProp?.(res);
    });
  });

  return {
    fields,
    onSubmit,
    setValue,
    getValues,
    watch,
    ...rest,
    directionOptions,
    paymentForms,
    paymentMethodOptions,
  };
};

export default useJuridicalContractForm;
